import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface FilterState {
  search?: string
  page?: number
  limit?: number
  [key: string]: any
}

export interface UseAdminFiltersOptions {
  initialFilters?: FilterState
  defaultLimit?: number
  debounceMs?: number
  syncWithUrl?: boolean
}

export function useAdminFilters(options: UseAdminFiltersOptions = {}) {
  const {
    initialFilters = {},
    defaultLimit = 10,
    debounceMs = 500,
    syncWithUrl = true,
  } = options

  const [searchParams, setSearchParams] = useSearchParams()

  // Extract initial values from URL params if available and enabled
  const getParam = useCallback((key: string, fallback: any) => {
    if (!syncWithUrl) return fallback
    const val = searchParams.get(key)
    if (val === null) return fallback
    if (key === 'page' || key === 'limit') {
      const num = parseInt(val, 10)
      return isNaN(num) ? fallback : num
    }
    return val
  }, [searchParams, syncWithUrl])

  // Internal State
  const [search, setSearchInput] = useState<string>(() => getParam('search', initialFilters.search || ''))
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)
  const [page, setPage] = useState<number>(() => getParam('page', initialFilters.page || 1))
  const [limit, setLimit] = useState<number>(() => getParam('limit', initialFilters.limit || defaultLimit))
  
  // Custom Filter Key-Values (e.g. status, categoryId, brandId, paymentStatus)
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const custom: Record<string, any> = { ...initialFilters }
    delete custom.search
    delete custom.page
    delete custom.limit

    if (syncWithUrl) {
      searchParams.forEach((value, key) => {
        if (key !== 'search' && key !== 'page' && key !== 'limit') {
          custom[key] = value
        }
      })
    }
    return custom
  })

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [search, debounceMs])

  // Sync state to URL searchParams
  useEffect(() => {
    if (!syncWithUrl) return

    const params = new URLSearchParams()
    if (page > 1) params.set('page', String(page))
    if (limit !== defaultLimit) params.set('limit', String(limit))
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim())

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'all') {
        params.set(key, String(value))
      }
    })

    // Compare with current searchParams using sorted entries to avoid redundant replaces
    const currentStr = Array.from(searchParams.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&')

    const newStr = Array.from(params.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&')

    if (currentStr !== newStr) {
      setSearchParams(params, { replace: true })
    }
  }, [debouncedSearch, page, limit, filters, syncWithUrl, defaultLimit, searchParams, setSearchParams])

  // Handle Search Change
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    setPage(1) // Reset page on search change
  }, [])

  // Handle Single Filter Change
  const handleFilterChange = useCallback((key: string, value: any) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (value === undefined || value === null || value === '' || value === 'all') {
        delete next[key]
      } else {
        next[key] = value
      }
      return next
    })
    setPage(1) // Reset page on filter change
  }, [])

  // Handle Clear All Filters
  const clearFilters = useCallback(() => {
    setSearchInput('')
    setDebouncedSearch('')
    setFilters({})
    setPage(1)
  }, [])

  // Handle Single Filter Removal
  const removeFilter = useCallback((key: string) => {
    if (key === 'search') {
      setSearchInput('')
      setDebouncedSearch('')
    } else {
      setFilters((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
    setPage(1)
  }, [])

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    if (debouncedSearch.trim() !== '') return true
    return Object.values(filters).some((val) => val !== undefined && val !== null && val !== '' && val !== 'all')
  }, [debouncedSearch, filters])

  // Aggregate current query params for API call
  const queryParams = useMemo(() => {
    const result: Record<string, any> = {
      page,
      limit,
      search: debouncedSearch.trim() || undefined,
    }
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        result[k] = v
      }
    })
    return result
  }, [page, limit, debouncedSearch, filters])

  return {
    search,
    debouncedSearch,
    page,
    limit,
    filters,
    queryParams,
    hasActiveFilters,
    setSearch: handleSearchChange,
    setPage,
    setLimit,
    setFilter: handleFilterChange,
    removeFilter,
    clearFilters,
  }
}
